import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { Pause } from 'lucide-react'
import { cn } from '@/lib/utils'
import EqualizerBars from '@/components/landing/equalizer-bars'
import { useSong } from '@/components/landing/song-provider'
import { SONG } from '@/constants/birthday'

// Small player that follows her down the page while the song plays.
const FloatingSongPill: FC = (): ReactNode => {
    const { playing, toggle } = useSong()
    const [songInView, setSongInView] = useState(true)

    useEffect(() => {
        const section = document.getElementById('song')

        if (!section || typeof IntersectionObserver === 'undefined') return

        const observer = new IntersectionObserver(([entry]) => {
            setSongInView(entry.isIntersecting)
        }, { threshold: 0.1 })

        observer.observe(section)

        return () => observer.disconnect()
    }, [])

    const visible = playing && !songInView

    return (
        <div
            aria-hidden={!visible}
            className={cn(
                'fixed bottom-4 right-4 z-40 transition duration-300 motion-reduce:transition-none sm:bottom-6 sm:right-6',
                visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-4 opacity-0'
            )}
        >
            <div className='flex items-center gap-3 rounded-full bg-wine/95 py-2 pl-4 pr-2 text-white shadow-xl shadow-wine/30 ring-1 ring-white/10 backdrop-blur'>
                <EqualizerBars playing={playing} className='text-rose-300' />

                <a
                    href='#song'
                    tabIndex={visible ? 0 : -1}
                    className='text-sm font-bold hover:underline focus-visible:underline focus-visible:outline-none'
                >
                    {SONG.name}
                </a>

                <button
                    type='button'
                    onClick={toggle}
                    tabIndex={visible ? 0 : -1}
                    aria-label={SONG.pause}
                    className='grid size-9 place-items-center rounded-full bg-primary text-primary-foreground transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white'
                >
                    <Pause className='size-4 fill-current' />
                </button>
            </div>
        </div>
    )
}

export default FloatingSongPill
